import { useEffect, useRef, useState } from "react";
import {
  createPaymentIntent,
  getJobPostingPriceDetails,
  getPaymentMethods,
} from "../../endpoints/stripe.service";
import { useSnackbar } from "notistack";
import VisaCardImg from "../../assets/images/Visa.svg";
import MaestroCardImage from "../../assets/images/Maestro.png";
import AmericanExpressCardImg from "../../assets/images/AmericanExpress.png";
import MasterCardImg from "../../assets/images/MasterCard.png";
import TickThinOutline from "../../assets/svg/TickThinOutline";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import env from "../../env";
import CardForm from "../../components/Cards/Card/CardForm";
import LoadingAnimation from "../../assets/svg/LoadingAnimation";
import { colors } from "../../utils/theme";

const stripePromise = loadStripe(env.publishableKey);

export const RecruiterCheckoutPage = ({ onPaymentSuccess, currentJobId }) => {
  const [cardLists, setCardLists] = useState([]);
  const recruiterCheckoutRef = useRef(false);
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPaymentDetails, setSelectedPaymentDetails] = useState(null);
  const [activeTab, setActiveTab] = useState("saved"); // 'saved' or 'new'
  const [priceDetails, setPriceDetails] = useState({});
  const [priceLoading, setPriceLoading] = useState(true);
  const [totalPrice, setTotalPrice] = useState(0);
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);

  const calculateProcessingFee = (amount) => {
    const percentageFee = amount * 0.015; // 1.5%
    const fixedFee = 0.2; // 20p
    return (percentageFee + fixedFee).toFixed(2);
  };

  const calculateTotal = (amount) => {
    const processingFee = parseFloat(calculateProcessingFee(amount));
    return (parseFloat(amount) + processingFee).toFixed(2);
  };

  const fetchCards = () => {
    getPaymentMethods("card")
      .then((response) => {
        if (response.success) setCardLists(response.data);
      })
      .catch((error) => enqueueSnackbar(error, { variant: "error" }))
      .finally(() => setIsLoading(false));
  };

  const fetchJobPostingPriceDetails = () => {
    getJobPostingPriceDetails()
      .then((response) => {
        if (response.success) {
          console.log("get job posting details response", response);
          const amount = response.data.amount;
          setPriceDetails(response.data);
          setTotalPrice(calculateTotal(amount));
        }
      })
      .catch((error) => enqueueSnackbar(error, { variant: "error" }))
      .finally(() => setPriceLoading(false));
  };

  useEffect(() => {
    if (recruiterCheckoutRef.current) return;
    recruiterCheckoutRef.current = true;

    fetchCards();
    fetchJobPostingPriceDetails();
  }, []);

  const handlePayment = (paymentMethodId) => {
    setIsPaymentLoading(true);
    const amount = totalPrice;
    const currency = priceDetails?.currency || "gbp";
    if (!totalPrice || totalPrice == 0) {
      enqueueSnackbar("Amount is required", { variant: "error" });
      return;
    }
    console.log("total", amount, currency, paymentMethodId);

    if (!paymentMethodId) {
      enqueueSnackbar("Please select payment method", { variant: "error" });
      return;
    }
    createPaymentIntent(amount, currency, paymentMethodId, currentJobId)
      .then(async (response) => {
        if (response.success) {
          if (onPaymentSuccess) {
            await onPaymentSuccess();
            enqueueSnackbar("Payment made successfully", {
              variant: "success",
            });
          }
        }
      })
      .catch((error) => enqueueSnackbar(error, { variant: "error" }))
      .finally(() => setIsPaymentLoading(false));
  };

  return (
    <div className="w-full h-full">
      <div className="bg-white w-full h-full">
        <div className="flex justify-center items-center">
          {/* Left side - Pricing Details */}
          <div className="w-1/3 border-r px-8 py-6">
            {priceLoading ? (
              <div className="gap-2.5 flex mt-4 flex-col">
                {Array.from({ length: 3 }, (x, i) => i).map((key) => (
                  <div key={key} className="flex justify-between">
                    <div className="flex rounded-lg w-full pl-3 pr-8 py-4 gap-1.5 bg-gray-100">
                      <div className="h-3 rounded bg-gray-200 w-1/4"></div>
                      <div className="h-3 rounded bg-gray-200 w-1/4"></div>
                      <div className="h-3 rounded bg-gray-200 w-1/4"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : Object.keys(priceDetails).length !== 0 &&
              priceDetails.constructor === Object ? (
              <>
                <h2 className="text-lg font-semibold mb-4">
                  Job Posting Details
                </h2>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600 text-sm">Base Price</span>
                    <span className="font-medium text-sm">
                      {/* {priceDetails.currency} */}£ {priceDetails.amount}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 text-sm">
                      Processing Fee (1.5% + 20p)
                    </span>
                    <span className="font-medium text-sm">
                      £ {calculateProcessingFee(priceDetails.amount)}
                    </span>
                  </div>
                  <div className="h-px bg-gray-200 my-2" />
                  <div className="flex justify-between">
                    <span className="font-semibold">Total</span>
                    <span className="font-semibold">£ {totalPrice}</span>
                  </div>
                </div>
              </>
            ) : (
              <>
                <h6>Job posting price details not found.</h6>
              </>
            )}
          </div>

          {/* Right side - Payment Methods */}
          <div className="w-2/3 px-8 py-6">
            <div className="flex space-x-1 mb-6">
              <button
                onClick={() => setActiveTab("saved")}
                className={
                  activeTab === "saved"
                    ? "px-4 py-2 rounded-full bg-secondary text-white"
                    : "px-4 py-2 rounded-full bg-gray-100 text-gray-600"
                }
              >
                Saved Cards
              </button>
              <button
                onClick={() => setActiveTab("new")}
                className={
                  activeTab === "new"
                    ? "px-4 py-2 rounded-full bg-secondary text-white"
                    : "px-4 py-2 rounded-full bg-gray-100 text-gray-600"
                }
              >
                Add New Card
              </button>
            </div>

            {/* Content based on active tab */}
            {activeTab === "saved" ? (
              isLoading ? (
                <div className="gap-2.5 flex mt-4 flex-col">
                  {Array.from({ length: 3 }, (x, i) => i).map((key) => (
                    <div key={key} className="flex justify-between">
                      <div className="flex rounded-lg w-full pl-3 pr-8 py-4 gap-1.5 bg-gray-100">
                        <div className="h-3 rounded bg-gray-200 w-1/4"></div>
                        <div className="h-3 rounded bg-gray-200 w-1/4"></div>
                        <div className="h-3 rounded bg-gray-200 w-1/4"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : cardLists.length > 0 ? (
                <div className="space-y-3">
                  {cardLists.map((card, index) => {
                    const isSelected = selectedPaymentDetails?.id === card?.id;
                    return (
                      <div
                        key={card.id}
                        onClick={() => {
                          if (selectedPaymentDetails?.id === card?.id) {
                            setSelectedPaymentDetails(null);
                          } else {
                            setSelectedPaymentDetails(card);
                          }
                        }}
                        className={
                          isSelected
                            ? "border rounded-lg p-4 cursor-pointer hover:bg-gray-50 border-secondary bg-orange-50"
                            : "border rounded-lg p-4 cursor-pointer hover:bg-gray-50"
                        }
                      >
                        <div className="flex items-center justify-start gap-10">
                          <div className="flex items-center gap-3">
                            <div
                              className={
                                isSelected
                                  ? "w-5 h-5 rounded-md border-2 border-secondary"
                                  : "w-5 h-5 rounded-md border-2"
                              }
                            >
                              {isSelected ? (
                                <TickThinOutline
                                  className={"w-4 h-4"}
                                  fill="#FF9800"
                                />
                              ) : null}
                            </div>
                            <img
                              src={(function () {
                                switch (card?.card?.brand.toLowerCase()) {
                                  case "visa":
                                    return VisaCardImg;
                                  case "mastercard":
                                    return MasterCardImg;
                                  case "amex":
                                    return AmericanExpressCardImg;
                                  case "maestro":
                                    return MaestroCardImage;
                                  default:
                                    return VisaCardImg;
                                }
                              })()}
                              alt={card?.card?.brand}
                              className="h-6"
                            />
                            <span className="text-sm text-primary">
                              **** **** **** {card?.card?.last4}
                            </span>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="text-sm text-gray-600">
                              {card?.card?.exp_month}/{card?.card?.exp_year}
                            </span>
                            <span className="text-sm text-gray-600">
                              {card?.billing_details?.name}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {selectedPaymentDetails && (
                    <button
                      className="flex items-center bg-secondary text-white rounded-lg py-2 font-semibold mt-4 px-4"
                      onClick={() => {
                        handlePayment(selectedPaymentDetails.id);
                      }}
                    >
                      {isPaymentLoading && (
                        <LoadingAnimation
                          color={colors.White}
                          className="w-4 h-4"
                        />
                      )}
                      Pay £ {totalPrice}
                    </button>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-600">No saved cards found.</p>
                  <button
                    onClick={() => setActiveTab("new")}
                    className="mt-4 bg-primary text-white rounded-lg py-2 font-semibold px-4"
                  >
                    Add a new card
                  </button>
                </div>
              )
            ) : (
              <Elements stripe={stripePromise}>
                <CardForm
                  sendCancel={() => {
                    setActiveTab("saved");
                    fetchCards();
                  }}
                />
              </Elements>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
